import React, { useState } from "react";
import styles from "./OrganizationForm.module.scss";
import cn from "classnames";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CIK_RE = /^[0-9]{1,10}$/;            // SEC EDGAR CIKs are up to 10 digits
const ETH_ADDR_RE = /^0x[0-9a-fA-F]{40}$/; // optional, only validated if filled

const validateField = (name, value, form) => {
  if (["name", "address", "externalId", "emailId"].includes(name) && !String(value || "").trim()) {
    return "Required";
  }
  if (name === "emailId" && value && !EMAIL_RE.test(value)) return "Enter a valid email";
  if (name === "externalId" && value && !CIK_RE.test(value)) return "CIK must be 1–10 digits";
  if (name === "walletAddress" && value && !ETH_ADDR_RE.test(value))
    return "Wallet must be a 0x-prefixed 40-char hex address";
  return null;
};

function OrganizationForm({ form, handleChange, handleSubmit, isUpdating, onCancel }) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const fields = [
    { key: "name",          label: "Name",       help: "Organization legal name" },
    { key: "address",       label: "Address",    help: "Mailing address" },
    { key: "externalId",    label: "CIK",        help: "SEC EDGAR Central Index Key (digits only)" },
    { key: "emailId",       label: "Email",      help: "Primary contact for sign-in" },
    { key: "walletAddress", label: "Wallet",     help: "Ethereum address used for note custody" },
    { key: "web3Id",        label: "Web3 ID",    help: "Optional identifier on chain" },
  ];

  const onBlur = (e) => {
    const { name, value } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((errs) => ({ ...errs, [name]: validateField(name, value, form) }));
  };

  const onChangeWithValidate = (e) => {
    handleChange(e);
    const { name, value } = e.target;
    if (touched[name]) {
      setErrors((errs) => ({ ...errs, [name]: validateField(name, value, form) }));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    let firstInvalid = null;
    fields.forEach(({ key }) => {
      const err = validateField(key, form[key], form);
      if (err) { next[key] = err; if (!firstInvalid) firstInvalid = key; }
    });
    setErrors(next);
    setTouched(Object.fromEntries(fields.map(({ key }) => [key, true])));
    if (firstInvalid) {
      const el = document.getElementById(firstInvalid);
      if (el) el.focus();
      return;
    }
    handleSubmit(e);
  };

  return (
    <form onSubmit={submit} className={styles.organizationFormContainer} noValidate>
      <div className={styles.organizationForm}>
        {fields.map(({ key, label, help }) => (
          <div key={key} className={styles.formGroup}>
            <label htmlFor={key}>{label}</label>
            <input
              type="text"
              id={key}
              name={key}
              value={form[key] || ""}
              onChange={onChangeWithValidate}
              onBlur={onBlur}
              aria-invalid={!!errors[key]}
              aria-describedby={`${key}-help`}
            />
            <small id={`${key}-help`} className={errors[key] ? styles.error : styles.help}>
              {errors[key] || help}
            </small>
          </div>
        ))}
        <div className={styles.formGroup}>
          <label htmlFor="type">Type</label>
          <select id="type" name="type" value={form.type} onChange={onChangeWithValidate} required>
            <option value="private">Private</option>
            <option value="public">Public</option>
            <option value="depositor">Depositor</option>
          </select>
          <small className={styles.help}>Organizational classification</small>
        </div>
      </div>

      <div className={styles.formActions}>
        <button type="button" onClick={onCancel} className={cn(styles.cancelButton, styles.button)}>
          Cancel
        </button>
        <button type="submit" className={cn(styles.submitButton, styles.button)}>
          {isUpdating ? "Save changes" : "Add organization"}
        </button>
      </div>
    </form>
  );
}

export default OrganizationForm;
